import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Download, Loader2, Play, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface Stream {
  url: string;
  name: string;
  title?: string;
}

interface StreamsListProps {
  streams?: Stream[];
  isLoading?: boolean;
  error?: Error | null;
  title?: string;
  onClose?: () => void;
}

export function StreamsList({
  streams = [],
  isLoading,
  error,
  title = "Available Streams",
  onClose,
}: StreamsListProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-destructive/10 border-destructive rounded-lg p-6">
        <p className="text-destructive">Error: {error.message}</p>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="border-b border-border">
        <CardTitle className="flex items-center gap-2">
          <Play className="h-4 w-4" />
          {title}
          {onClose && (
            <Button
              variant="ghost"
              size="icon"
              className="ml-auto"
              onClick={onClose}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </CardTitle>
        <CardDescription>
          Choose from {streams.length} available streams
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <div
          className="max-h-[calc(100vh-100px)] overflow-y-auto [mask-image:linear-gradient(to_bottom,black_calc(100%-40px),transparent)] after:content-[''] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-10 after:bg-gradient-to-t after:from-background after:to-transparent after:pointer-events-none pb-5"
          key={streams.length}
        >
          {streams
            .filter((stream) => "url" in stream)
            .map((stream) => (
              <div
                key={stream.url}
                className="overflow-hidden border-b border-border"
              >
                <div className="p-4">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <h3 className="font-semibold">{stream.name}</h3>
                      <p className="text-sm text-muted-foreground break-all overflow-hidden max-w-full">
                        {stream.title?.split("\n").map((line, i, arr) => (
                          <span key={i} className="block">
                            {line}
                          </span>
                        ))}
                      </p>
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button
                        variant="secondary"
                        size="icon"
                        asChild
                        className={cn(
                          "group relative animate-rainbow cursor-pointer border-0 font-medium transition-colors [background-clip:padding-box,border-box,border-box] [background-origin:border-box] [border:calc(0.08*1rem)_solid_transparent] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 text-primary-foreground",
                          "before:absolute before:bottom-[-20%] before:left-1/2 before:z-0 before:h-1/5 before:w-3/5 before:-translate-x-1/2 before:animate-rainbow before:bg-[linear-gradient(90deg,hsl(var(--color-1)),hsl(var(--color-5)),hsl(var(--color-3)),hsl(var(--color-4)),hsl(var(--color-2)))] before:[filter:blur(calc(0.8*1rem))]",
                          "bg-[linear-gradient(#121213,#121213),linear-gradient(#121213_50%,rgba(18,18,19,0.6)_80%,rgba(18,18,19,0)),linear-gradient(90deg,hsl(var(--color-1)),hsl(var(--color-5)),hsl(var(--color-3)),hsl(var(--color-4)),hsl(var(--color-2)))]",
                          "dark:bg-[linear-gradient(#fff,#fff),linear-gradient(#fff_50%,rgba(255,255,255,0.6)_80%,rgba(0,0,0,0)),linear-gradient(90deg,hsl(var(--color-1)),hsl(var(--color-5)),hsl(var(--color-3)),hsl(var(--color-4)),hsl(var(--color-2)))]"
                        )}
                      >
                        <a
                          href={`iina://open?url=${encodeURIComponent(
                            new URL(stream.url, location.origin).toString()
                          )}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Play className="h-4 w-4" />
                        </a>
                      </Button>
                      <Button variant="outline" size="icon" asChild>
                        <a
                          href={stream.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Download className="h-4 w-4" />
                        </a>
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
        </div>
      </CardContent>
    </Card>
  );
}
